import { useCallback, useMemo } from 'react';
import { TIconName } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import { ISecureSCAModalProps } from '@/features/sca/components/SecureSCAModal';
import { useProfileContext } from '@/features/context/ProfileContext';
import { IScaWallet, TSCAWAlletActions, TLockReason } from '@/store/models/sca.model';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';
import { useAccesControl } from '@/hooks/useAccessControl';
import { deleteSCAWallet, resetPIN, lockSCAWallet, unlockSCA } from '@/store/actions/sca.actions';
import { openModal } from '@/store/actions/core.actions';

import { featuresSlugs } from '@/features/roles/store/models/features.model';

import { useSCAStatus } from './useSCAStatus';

interface IUseSCAActionsProps {
  scaWallet: IScaWallet;
}

export function useSCAActions({ scaWallet }: IUseSCAActionsProps) {
  const { user } = useProfileContext();
  const { t } = useTranslation();
  const { hasAccessToFeature } = useAccesControl();

  const dispatch = useAppDispatch();
  const { scaStatus } = useSCAStatus({ scaWallet });

  const handleUnlockSCA = () => {
    scaWallet?.scaWalletId && dispatch(unlockSCA(user.userId, scaWallet.scaWalletId));
  };

  const handleSecureScaWallet = useCallback(
    (lockReason: TLockReason, lockMessage?: string) => {
      scaWallet?.scaWalletId && dispatch(lockSCAWallet(user.userId, scaWallet.scaWalletId, lockReason, lockMessage));
    },
    [scaWallet, dispatch, user.userId],
  );

  const handleResetPIN = () => {
    scaWallet?.scaWalletId && dispatch(resetPIN(user.userId, scaWallet.scaWalletId));
  };

  const handleDelete = () => {
    scaWallet?.scaWalletId && dispatch(deleteSCAWallet(user.userId, scaWallet.scaWalletId));
  };

  const toggleSecureScaWalletDialog = useCallback(() => {
    dispatch(
      openModal<ISecureSCAModalProps>('secureSCAModal', {
        onSubmit: handleSecureScaWallet,
        userId: user.userId,
        scaWalletId: scaWallet.scaWalletId,
      }),
    );
  }, [dispatch, handleSecureScaWallet, user.userId, scaWallet.scaWalletId]);

  const ConfirmDelete = useConfirmationDialog({
    title: t('pages.currentProfiles.profile.sca.delete.title'),
    highlight: t('pages.currentProfiles.profile.sca.common.highlight'),
    description: t('pages.currentProfiles.profile.sca.delete.description'),
    confirm: { label: t('pages.modal.sure'), action: handleDelete, color: 'danger' },
    cancel: { label: t('pages.modal.closebtn') },
  });

  const ConfirmResetPIN = useConfirmationDialog({
    title: t('pages.currentProfiles.profile.sca.reset.title'),
    highlight: t('pages.currentProfiles.profile.sca.common.highlight'),
    description: t('pages.currentProfiles.profile.sca.reset.description'),
    confirm: { label: t('pages.modal.sure'), action: handleResetPIN },
    cancel: { label: t('pages.modal.closebtn') },
  });

  const ConfirmUnlockSCA = useConfirmationDialog({
    title: t('pages.currentProfiles.profile.sca.unlock.title'),
    highlight: t('pages.currentProfiles.profile.sca.common.highlight'),
    description: t('pages.currentProfiles.profile.sca.unlock.subDescription'),
    confirm: { label: t('pages.modal.sure'), action: handleUnlockSCA },
    cancel: { label: t('pages.modal.closebtn') },
  });

  const actions: Record<
    TSCAWAlletActions,
    {
      icon: TIconName;
      onClick: () => void;
      tooltip?: string;
    }
  > = {
    LOCK: {
      icon: 'lock',
      onClick: toggleSecureScaWalletDialog,
      tooltip: t('pages.currentProfiles.profile.sca.actions.lock'),
    },
    UNLOCK: {
      icon: 'lock_open',
      onClick: ConfirmUnlockSCA.toggle,
      tooltip: t('pages.currentProfiles.profile.sca.actions.unlock'),
    },
    DELETE: {
      icon: 'delete',
      onClick: ConfirmDelete.toggle,
      tooltip: t('pages.currentProfiles.profile.sca.actions.delete'),
    },
    RESET_PIN: {
      icon: 'password',
      onClick: ConfirmResetPIN.toggle,
      tooltip: t('pages.currentProfiles.profile.sca.actions.reset'),
    },
  };

  const actionList: TSCAWAlletActions[] = useMemo(() => {
    if (!hasAccessToFeature(featuresSlugs.SCA_ON_WALLET)) {
      return [];
    }
    switch (scaStatus) {
      case 'LOCKED':
        return ['UNLOCK', 'RESET_PIN', 'DELETE'];
      case 'PASSCODE_MISSING':
        return ['DELETE'];
      case 'DELETED':
        return [];
      default:
        return ['LOCK', 'RESET_PIN', 'DELETE'];
    }
  }, [hasAccessToFeature, scaStatus]);
  return { actions, actionList, ConfirmDelete, ConfirmResetPIN, ConfirmUnlockSCA };
}
